import React from 'react'
import { Box, Typography, styled } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { Button, Chip, FormGroup } from '@/design-system'
import { useAuth } from '@/hooks/common'
import { useSubscription } from '@/hooks/common/useSubscription'

const PanelHead = styled(Box)({ marginBottom: 18 })
const H2 = styled(Typography)({ fontSize: 20, fontWeight: 720, letterSpacing: '-0.025em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13, color: 'var(--ds-ink-subtle)', marginTop: 4 })

const HeadRow = styled(Box)({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 })
const GroupTitle = styled('h3')({ fontSize: 15, fontWeight: 700, letterSpacing: '-0.015em', color: 'var(--ds-ink)', margin: 0 })

const PlanCard = styled(Box)({
  borderRadius: 'var(--ds-radius-md)',
  border: '2px solid transparent',
  background: 'linear-gradient(var(--ds-canvas), var(--ds-canvas)) padding-box, var(--ds-gradient-spectrum, var(--ds-ink)) border-box',
  padding: '22px 24px',
})

const PlanName = styled('div')({ fontSize: 20, fontWeight: 750, letterSpacing: '-0.03em', color: 'var(--ds-ink)' })
const Renews = styled('div')({ fontSize: 12.5, color: 'var(--ds-ink-subtle)', marginTop: 4 })

const Feats = styled(Box)({ marginTop: 18, display: 'flex', flexDirection: 'column' })
const Feat = styled(Box)({ display: 'flex', gap: 9, padding: '6px 0', fontSize: 13, color: 'var(--ds-ink-muted)' })
const Check = styled('span')({ color: 'var(--ds-accent-deep-a, var(--ds-ink))', fontWeight: 700, flexShrink: 0 })

const CtaRow = styled(Box)({ display: 'flex', gap: 10, marginTop: 18 })

const Empty = styled(Box)({ textAlign: 'center', padding: '34px 0' })

const FREE_FEATURES = ['3 albums, unlimited viewers', 'Full resolution — always', 'Reactions, comments & pins']
const PRO_FEATURES = [
  'Unlimited albums & unlimited keys',
  '3D walkable galleries — 14 Classic scenes + Studio presets',
  'Reactions, comments & pins',
  'Share links + QR',
]
const ENTERPRISE_FEATURES = [
  ...PRO_FEATURES,
  'Priority support',
]

// payments-infra only knows the generic 'free' | 'pro' | 'enterprise' tier names —
// these are Egowall's actual plan names (see PricingPage's PLANS).
const TIER_LABELS: Record<string, string> = { free: 'Keepsake', pro: 'Gallery', enterprise: 'Studio' }

/** Plan, renewal, and portal access — same `useSubscription` hook the legacy profile pages used. */
export function BillingSection() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { subscription, loading, tier, isPro, openPortal, portalLoading } = useSubscription(user?.userID)

  const planLabel = TIER_LABELS[tier] ?? tier.charAt(0).toUpperCase() + tier.slice(1)
  const renewalDate = subscription?.currentPeriodEnd
    ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null
  const cancelling = !!subscription?.cancelAtPeriodEnd
  const statusActive = subscription?.status === 'active' && !cancelling

  return (
    <Box>
      <PanelHead>
        <H2>Billing</H2>
        <Sub>Your plan, renewal, and invoice history.</Sub>
      </PanelHead>

      <FormGroup>
        <HeadRow>
          <GroupTitle>Current plan</GroupTitle>
          <Chip
            tone='neutral'
            sx={statusActive ? { backgroundColor: 'rgba(1,218,254,0.14)', color: '#0891a8' } : undefined}
          >
            {statusActive ? 'ACTIVE' : cancelling ? 'CANCELS AT PERIOD END' : (subscription?.status ?? 'FREE').toUpperCase()}
          </Chip>
        </HeadRow>

        <Box sx={{ marginTop: 2 }}>
          <PlanCard>
            <PlanName>{loading ? '…' : planLabel}</PlanName>
            {renewalDate && <Renews>{cancelling ? `Ends ${renewalDate}` : `Renews ${renewalDate}`}</Renews>}

            <Feats>
              {(tier === 'enterprise' ? ENTERPRISE_FEATURES : isPro ? PRO_FEATURES : FREE_FEATURES).map((f) => (
                <Feat key={f}>
                  <Check>✓</Check>
                  <span>{f}</span>
                </Feat>
              ))}
            </Feats>

            <CtaRow>
              {isPro ? (
                <Button variant='secondary' size='sm' loading={portalLoading} onClick={openPortal}>
                  ⚙ Manage billing
                </Button>
              ) : (
                <Button variant='spectrum' size='sm' onClick={() => navigate('/pricing')}>
                  Upgrade
                </Button>
              )}
              <Button variant='ghost' size='sm' onClick={() => navigate('/pricing')}>
                View all plans
              </Button>
            </CtaRow>
          </PlanCard>
        </Box>
      </FormGroup>

      {!isPro && (
        <FormGroup label='Billing history'>
          <Empty>
            <Typography sx={{ fontSize: 30, opacity: 0.35, marginBottom: 1.25 }}>🧾</Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 640, color: 'var(--ds-ink-muted)' }}>No billing history yet</Typography>
            <Typography sx={{ fontSize: 12, color: 'var(--ds-ink-subtle)', marginTop: 0.5 }}>
              Your invoices will appear here once you upgrade.
            </Typography>
          </Empty>
        </FormGroup>
      )}
    </Box>
  )
}
